/**
 * convertToBlocks - Scanned pages to editor blocks
 *
 * Converts loaded book data to BlockEditor format with 1:1 page mapping.
 * Each scanned story/text/chapter page becomes one editor block, preserving
 * the original page distribution from the physical book.
 *
 * Pages marked is_two_page_spread already contain both pages' text from a
 * single capture and map to one block as-is.
 *
 * Agents can manually split/merge blocks after initial load.
 *
 * Extracted from BookContentEditor.tsx for testability.
 */

import type { BookContentForEditor } from '@/services/scan/content-editor';
import { type DigitalPageBlock, textToLines, generateBlockId } from './types';

/**
 * Convert loaded book data to DigitalPageBlock[].
 *
 * Priority:
 * 1. If digital_page_content already exists, use it (returning from a saved session)
 * 2. Otherwise, create one block per story/text/chapter page (1:1 mapping)
 */
export function convertToBlocks(book: BookContentForEditor): DigitalPageBlock[] {
  if (book.digitalPageContent && book.digitalPageContent.length > 0) {
    return book.digitalPageContent.map((page, index) => ({
      id: generateBlockId(),
      pageNumber: page.digitalPageNumber || index + 1,
      lines: textToLines(page.text),
      sourcePageIds: [page.sourcePageId],
    }));
  }

  const storyPages = book.pages.filter(p =>
    ['story', 'text', 'chapter'].includes(p.pageType) && p.extractedText
  );

  if (storyPages.length === 0) {
    return [{
      id: generateBlockId(),
      pageNumber: 1,
      lines: [{ text: '', indent: 0 }],
      sourcePageIds: book.pages.map(p => p.id),
    }];
  }

  return storyPages.map((page, index) => ({
    id: generateBlockId(),
    pageNumber: index + 1,
    lines: textToLines(page.extractedText),
    sourcePageIds: [page.id],
  }));
}
